// PropertyCardActions — slim icon-only quick-action row added directly
// beneath the "View hotel" CTA on every property card.
//
// Four equal-weight icon buttons:
//   1. Email envelope    → routes to /contact
//   2. Phone             → placeholder anchor (href intentionally blank
//                          until each property's number is provisioned)
//   3. Virtual assistant → fires a `xaluca:open-chat` window event that
//                          opens the global chatbot widget
//   4. Share             → opens a small dialog (Email · Copy link ·
//                          WhatsApp) targeted at the property's page
//
// All clicks stopPropagation + preventDefault so they never trigger the
// surrounding card-wide `<Link>` navigation.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  Mail, Phone, Headset, Share2, Wallet, BedDouble, MapPin, Navigation, Car,
  Copy, Check, MessageCircle, X, Apple,
} from "lucide-react";
import { MapContainer, Marker, ZoomControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import MapTileToggle from "@/components/maps/MapTileToggle";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFormatPrice, useCurrency } from "@/contexts/CurrencyContext";
import { getHotelDetails } from "@/lib/hotelDetails";
import { getRoomPricing } from "@/lib/hotelPricing";
import { getHotelLocation } from "@/lib/hotelLocations";
import { isBivouac } from "@/lib/propertyType";

const stop = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

const ICON_BTN =
  "inline-flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 " +
  "border border-[#FDFBF7]/35 text-[#FDFBF7]/95 hover:border-[#D4A373] " +
  "hover:bg-[#D4A373]/15 hover:text-[#D4A373] transition-colors " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A373]";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const PRICING_COPY = {
  title_room:     { en: "Public room rates",         fr: "Tarifs publics des chambres",  es: "Tarifas públicas de habitaciones" },
  title_tent:     { en: "Public tent rates",         fr: "Tarifs publics des tentes",    es: "Tarifas públicas de tiendas" },
  subtitle_room:  {
    en: (cur) => `Per room · per night · in ${cur}`,
    fr: (cur) => `Par chambre · par nuit · en ${cur}`,
    es: (cur) => `Por habitación · por noche · en ${cur}`,
  },
  subtitle_tent:  {
    en: (cur) => `Per tent · per night · in ${cur}`,
    fr: (cur) => `Par tente · par nuit · en ${cur}`,
    es: (cur) => `Por tienda · por noche · en ${cur}`,
  },
  room:        { en: "Room",   fr: "Chambre",  es: "Habitación" },
  tent:        { en: "Tent",   fr: "Tente",    es: "Tienda" },
  room_only:   { en: "Room Only", fr: "Chambre seule", es: "Solo habitación" },
  tent_only:   { en: "Tent Only", fr: "Tente seule",   es: "Solo tienda" },
  breakfast:   { en: "Bed & Breakfast", fr: "Petit-déjeuner",   es: "Desayuno" },
  half_board:  { en: "Half Board",      fr: "Demi-pension",     es: "Media pensión" },
  full_board:  { en: "Full Board",      fr: "Pension complète", es: "Pensión completa" },
  on_request:  { en: "On request",      fr: "Sur demande",      es: "Bajo petición" },
  disclaimer: {
    en: "Indicative public rates, excluding tourism tax. Agency contracts and group rates on request.",
    fr: "Tarifs publics indicatifs, hors taxe de séjour. Tarifs agence et groupes sur demande.",
    es: "Tarifas públicas indicativas, excluida la tasa turística. Tarifas de agencia y grupos bajo petición.",
  },
  empty: {
    en: "Rates for this property are currently available on request.",
    fr: "Les tarifs de cette adresse sont disponibles sur demande.",
    es: "Las tarifas de esta dirección están disponibles bajo petición.",
  },
  close: { en: "Close", fr: "Fermer", es: "Cerrar" },
  view_full: { en: "View the full hotel page", fr: "Voir la page complète de l'hôtel", es: "Ver la página completa del hotel" },
};

const BOARD_KEYS = ["room_only", "breakfast", "half_board", "full_board"];

const PricingDialog = ({ hotelSlug, hotelName, onClose, testIdPrefix }) => {
  const { lang } = useLanguage();
  const { fromMAD } = useFormatPrice();
  const { currency } = useCurrency();
  const camp = isBivouac(hotelSlug);
  const COPY = {
    title:    camp ? PRICING_COPY.title_tent    : PRICING_COPY.title_room,
    subtitle: camp ? PRICING_COPY.subtitle_tent : PRICING_COPY.subtitle_room,
    room:        camp ? PRICING_COPY.tent       : PRICING_COPY.room,
    room_only:   camp ? PRICING_COPY.tent_only  : PRICING_COPY.room_only,
    breakfast:   PRICING_COPY.breakfast,
    half_board:  PRICING_COPY.half_board,
    full_board:  PRICING_COPY.full_board,
    on_request:  PRICING_COPY.on_request,
    disclaimer:  PRICING_COPY.disclaimer,
    empty:       PRICING_COPY.empty,
    close:       PRICING_COPY.close,
    view_full:   PRICING_COPY.view_full,
  };

  const rows = useMemo(() => {
    const d = getHotelDetails(hotelSlug);
    const rooms = (d?.rooms || []).filter(Boolean);
    return rooms
      .map((room) => {
        const pricing = getRoomPricing(room, hotelSlug);
        return { room, pricing };
      })
      .filter((r) => r.pricing && r.pricing.plans);
  }, [hotelSlug]);

  // Lock body scroll + close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${pick(PRICING_COPY.title, lang)} — ${hotelName}`}
      onClick={(e) => { e.stopPropagation(); onClose(); }}
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-[#1A1513]/82 backdrop-blur-sm px-3 sm:px-6 py-3 sm:py-10"
      data-testid={`${testIdPrefix}-pricing-dialog`}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl max-h-[90vh] flex flex-col bg-[#FDFBF7] border border-[#D4A373]/35 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.5)]"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-5 sm:px-8 pt-6 sm:pt-7 pb-4 border-b border-[#2C2621]/10">
          <div className="min-w-0">
            <p className="overline !text-[#C16542] flex items-center gap-2">
              <BedDouble className="w-3.5 h-3.5" strokeWidth={1.6} />
              {pick(PRICING_COPY.title, lang)}
            </p>
            <h3 className="font-serif-x mt-2 text-2xl sm:text-3xl text-[#2C2621] leading-tight truncate">
              {hotelName}
            </h3>
            <p className="mt-1 text-[11px] tracking-[0.22em] uppercase text-[#5C5248]">
              {(COPY.subtitle[lang] || COPY.subtitle.en)(currency)}
            </p>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="shrink-0 -mt-1 -mr-1 inline-flex items-center justify-center w-9 h-9 text-[#5C5248] hover:text-[#C16542] border border-[#2C2621]/15 hover:border-[#C16542] transition-colors"
            aria-label={pick(PRICING_COPY.close, lang)}
            data-testid={`${testIdPrefix}-pricing-close`}
          >
            <X className="w-4 h-4" strokeWidth={1.6} />
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-8 py-4 sm:py-5">
          {rows.length === 0 ? (
            <p className="text-sm italic text-[#5C5248] py-6" data-testid={`${testIdPrefix}-pricing-empty`}>
              {pick(PRICING_COPY.empty, lang)}
            </p>
          ) : (
            <>
              {/* Desktop / tablet: real table */}
              <table
                className="hidden sm:table w-full text-sm border-collapse"
                data-testid={`${testIdPrefix}-pricing-table`}
              >
                <thead>
                  <tr className="text-left text-[10px] tracking-[0.22em] uppercase text-[#5C5248]/85">
                    <th className="py-3 pr-4 font-medium">{pick(PRICING_COPY.room, lang)}</th>
                    {BOARD_KEYS.map((k) => (
                      <th key={k} className="py-3 px-2 font-medium text-right whitespace-nowrap">
                        {pick(PRICING_COPY[k], lang)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E8DFCF]">
                  {rows.map(({ room, pricing }, i) => (
                    <tr key={`pricing-row-${i}`} data-testid={`${testIdPrefix}-pricing-row-${i}`}>
                      <td className="py-3.5 pr-4 align-top">
                        <p className="font-serif-x text-base text-[#2C2621] leading-snug">
                          {pick(room.name, lang)}
                        </p>
                        {room.surface && (
                          <p className="mt-0.5 text-[10px] tracking-[0.2em] uppercase text-[#5C5248]/75">
                            {typeof room.surface === "string" ? room.surface : pick(room.surface, lang)}
                          </p>
                        )}
                      </td>
                      {BOARD_KEYS.map((k) => {
                        const amount = fromMAD(pricing.plans?.[k]);
                        return (
                          <td
                            key={k}
                            className="py-3.5 px-2 text-right tabular-nums whitespace-nowrap text-[#2C2621]"
                          >
                            {amount || (
                              <span className="text-[10px] tracking-[0.22em] uppercase text-[#5C5248]/60">
                                {pick(PRICING_COPY.on_request, lang)}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile: per-room card stack so columns don't overflow */}
              <ul className="sm:hidden divide-y divide-[#E8DFCF]" data-testid={`${testIdPrefix}-pricing-mobile-list`}>
                {rows.map(({ room, pricing }, i) => (
                  <li key={`pricing-mob-${i}`} className="py-4">
                    <p className="font-serif-x text-base text-[#2C2621] leading-snug">
                      {pick(room.name, lang)}
                    </p>
                    {room.surface && (
                      <p className="mt-0.5 text-[10px] tracking-[0.2em] uppercase text-[#5C5248]/75">
                        {typeof room.surface === "string" ? room.surface : pick(room.surface, lang)}
                      </p>
                    )}
                    <dl className="mt-3 space-y-2">
                      {BOARD_KEYS.map((k) => {
                        const amount = fromMAD(pricing.plans?.[k]);
                        return (
                          <div key={k} className="flex items-baseline justify-between gap-3">
                            <dt className="text-[10px] tracking-[0.22em] uppercase text-[#5C5248]">
                              {pick(PRICING_COPY[k], lang)}
                            </dt>
                            <dd className="text-sm tabular-nums whitespace-nowrap text-[#2C2621]">
                              {amount || (
                                <span className="text-[10px] tracking-[0.22em] uppercase text-[#5C5248]/60">
                                  {pick(PRICING_COPY.on_request, lang)}
                                </span>
                              )}
                            </dd>
                          </div>
                        );
                      })}
                    </dl>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 sm:px-8 py-4 border-t border-[#2C2621]/10 bg-[#F2EBE1]/40 flex flex-wrap items-center justify-between gap-3">
          <p className="text-[11px] text-[#5C5248] leading-relaxed max-w-md">
            {pick(PRICING_COPY.disclaimer, lang)}
          </p>
          <Link
            to={`/hotels/${hotelSlug}`}
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#C16542] hover:text-[#A35133] border-b border-[#C16542]/40 hover:border-[#A35133] pb-0.5"
            data-testid={`${testIdPrefix}-pricing-view-hotel`}
          >
            {pick(PRICING_COPY.view_full, lang)}
          </Link>
        </div>
      </div>
    </div>,
    document.body
  );
};

const DIRECTIONS_COPY = {
  title:    { en: "Directions",       fr: "Itinéraire",        es: "Cómo llegar" },
  subtitle: { en: "Address · contact · how to get there", fr: "Adresse · contact · comment venir", es: "Dirección · contacto · cómo llegar" },
  address:  { en: "Address",          fr: "Adresse",           es: "Dirección" },
  contact:  { en: "Contact",          fr: "Contact",           es: "Contacto" },
  phone:    { en: "Phone",            fr: "Téléphone",         es: "Teléfono" },
  email:    { en: "Email",            fr: "E-mail",            es: "Correo" },
  how:      { en: "How to get there", fr: "Comment venir",     es: "Cómo llegar" },
  by_car:   { en: "By car",           fr: "En voiture",        es: "En coche" },
  by_transport: { en: "Public transport", fr: "Transports publics", es: "Transporte público" },
  by_air:   { en: "From the airport", fr: "Depuis l'aéroport", es: "Desde el aeropuerto" },
  gmaps:    { en: "Open in Google Maps", fr: "Ouvrir dans Google Maps", es: "Abrir en Google Maps" },
  apple:    { en: "Open in Apple Maps",  fr: "Ouvrir dans Plans",        es: "Abrir en Mapas (Apple)" },
  waze:     { en: "Open in Waze",     fr: "Ouvrir dans Waze",  es: "Abrir en Waze" },
  map_label:{ en: "Map", fr: "Carte", es: "Mapa" },
  close:    { en: "Close",            fr: "Fermer",            es: "Cerrar" },
  copied:   { en: "Address copied",   fr: "Adresse copiée",    es: "Dirección copiada" },
};

const DirectionsDialog = ({ hotelSlug, hotelName, onClose, testIdPrefix }) => {
  const { lang } = useLanguage();
  const [copied, setCopied] = useState(false);
  const closeBtnRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  const loc = useMemo(() => {
    const d = getHotelDetails(hotelSlug);
    return getHotelLocation(hotelSlug, { location: d?.location || hotelName });
  }, [hotelSlug, hotelName]);

  const [lat, lng] = loc.coords;
  const address = pick(loc.address, lang);
  const phone = loc.phone;
  const email = loc.email;

  const gmapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&destination_place_id=${encodeURIComponent(hotelName)}`;
  const appleMapsUrl = `https://maps.apple.com/?daddr=${lat},${lng}&q=${encodeURIComponent(hotelName)}`;
  const wazeUrl = `https://waze.com/ul?ll=${lat}%2C${lng}&navigate=yes&z=10`;

  // Branded marker for the embedded Leaflet map.
  const markerIcon = useMemo(
    () =>
      L.divIcon({
        className: "xaluca-directions-marker",
        html: `
          <div style="position:relative;display:flex;align-items:center;justify-content:center;">
            <span style="
              width:34px;height:34px;border-radius:50%;
              background:#C16542;
              box-shadow:0 6px 16px rgba(193,101,66,0.55),0 0 0 5px rgba(212,163,115,0.55),0 0 0 8px rgba(253,251,247,0.95);
              display:flex;align-items:center;justify-content:center;
              color:#FDFBF7;font-weight:600;font-size:15px;font-family:'Outfit',sans-serif;
              transform:translateY(-50%);
            ">▲</span>
          </div>
        `,
        iconSize: [34, 34],
        iconAnchor: [17, 34],
      }),
    []
  );

  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus the close button on open (initial keyboard anchor).
    const focusTimer = window.setTimeout(() => {
      if (closeBtnRef.current) closeBtnRef.current.focus();
    }, 30);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      window.clearTimeout(focusTimer);
      // Restore focus to the element that opened the dialog.
      const prevFocused = previouslyFocusedRef.current;
      if (prevFocused && typeof prevFocused.focus === "function") {
        prevFocused.focus();
      }
    };
  }, [onClose]);

  // Simple focus trap — keep Tab cycling inside the dialog.
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== "Tab") return;
      const root = document.querySelector(`[data-dialog-root="${testIdPrefix}"]`);
      if (!root) return;
      const focusable = root.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input, textarea, select'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [testIdPrefix]);

  const copyAddress = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(`${hotelName} — ${address}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* noop */
    }
  };

  const dialog = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${pick(DIRECTIONS_COPY.title, lang)} — ${hotelName}`}
      onClick={(e) => { e.stopPropagation(); onClose(); }}
      className="fixed inset-0 z-[2147483000] flex items-end sm:items-center justify-center bg-[#1A1513]/82 backdrop-blur-sm px-3 sm:px-6 py-3 sm:py-10"
      data-testid={`${testIdPrefix}-directions-dialog`}
      data-dialog-root={testIdPrefix}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl max-h-[92vh] flex flex-col bg-[#FDFBF7] border border-[#D4A373]/35 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.5)]"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-5 sm:px-8 pt-6 sm:pt-7 pb-4 border-b border-[#2C2621]/10">
          <div className="min-w-0">
            <p className="overline !text-[#C16542] flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" strokeWidth={1.6} />
              {pick(DIRECTIONS_COPY.title, lang)}
            </p>
            <h3 className="font-serif-x mt-2 text-2xl sm:text-3xl text-[#2C2621] leading-tight truncate">
              {hotelName}
            </h3>
            <p className="mt-1 text-[11px] tracking-[0.22em] uppercase text-[#5C5248]">
              {pick(DIRECTIONS_COPY.subtitle, lang)}
            </p>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="shrink-0 -mt-1 -mr-1 inline-flex items-center justify-center w-9 h-9 text-[#5C5248] hover:text-[#C16542] border border-[#2C2621]/15 hover:border-[#C16542] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C16542]"
            aria-label={pick(DIRECTIONS_COPY.close, lang)}
            data-testid={`${testIdPrefix}-directions-close`}
          >
            <X className="w-4 h-4" strokeWidth={1.6} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-8 py-5 space-y-6 text-[#2C2621]">
          {/* Interactive map */}
          <section data-testid={`${testIdPrefix}-directions-map`}>
            <p className="overline !text-[#5C5248] mb-2">{pick(DIRECTIONS_COPY.map_label, lang)}</p>
            <div className="relative h-[220px] sm:h-[260px] md:h-[300px] border border-[#2C2621]/10 overflow-hidden bg-[#F2EBE1]">
              <MapContainer
                center={[lat, lng]}
                zoom={13}
                scrollWheelZoom={false}
                zoomControl={false}
                attributionControl={false}
                style={{ height: "100%", width: "100%" }}
                data-testid={`${testIdPrefix}-directions-leaflet`}
              >
                {/* Satellite / Map toggle — matches every other map on
                    the site (DirectionsPage, HotelLocationContact,
                    MapSection, MoroccoRegionsMap). Defaults to
                    satellite so the arrival landmark reads at a
                    glance, but lets the visitor switch back to the
                    branded Voyager map for street-level orientation.
                    The toggle button is Leaflet-native (top-right
                    corner), so it scales identically across desktop,
                    tablet and mobile viewports. */}
                <MapTileToggle
                  mapUrl="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                  mapAttribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                  mapSubdomains={['a', 'b', 'c', 'd']}
                  mapMaxZoom={20}
                />
                <ZoomControl position="bottomright" />
                <Marker position={[lat, lng]} icon={markerIcon} />
              </MapContainer>
            </div>
          </section>

          {/* Address */}
          <section data-testid={`${testIdPrefix}-directions-address`}>
            <p className="overline !text-[#5C5248] mb-2">{pick(DIRECTIONS_COPY.address, lang)}</p>
            <div className="flex items-start justify-between gap-3">
              <p className="text-base leading-relaxed">{address}</p>
              <button
                type="button"
                onClick={copyAddress}
                className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#2C2621]/15 hover:border-[#C16542] text-[10px] tracking-[0.22em] uppercase text-[#5C5248] hover:text-[#C16542] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C16542]"
                data-testid={`${testIdPrefix}-directions-copy`}
              >
                {copied ? (
                  <>
                    <Check className="w-3 h-3" strokeWidth={1.8} />
                    {pick(DIRECTIONS_COPY.copied, lang)}
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" strokeWidth={1.8} />
                    {pick({ en: "Copy", fr: "Copier", es: "Copiar" }, lang)}
                  </>
                )}
              </button>
            </div>
            <p className="mt-2 text-[11px] tracking-[0.2em] uppercase text-[#5C5248]/70 tabular-nums">
              {lat.toFixed(4)}°N · {Math.abs(lng).toFixed(4)}°{lng < 0 ? "W" : "E"}
            </p>
          </section>

          {/* Contact */}
          <section data-testid={`${testIdPrefix}-directions-contact`} className="grid sm:grid-cols-2 gap-5">
            <div>
              <p className="overline !text-[#5C5248] mb-2">{pick(DIRECTIONS_COPY.phone, lang)}</p>
              <a
                href={`tel:${phone.replace(/\s+/g, "")}`}
                className="inline-flex items-center gap-2 text-base text-[#2C2621] hover:text-[#C16542] transition-colors"
                data-testid={`${testIdPrefix}-directions-phone`}
              >
                <Phone className="w-3.5 h-3.5 text-[#C16542]" strokeWidth={1.6} />
                {phone}
              </a>
            </div>
            <div>
              <p className="overline !text-[#5C5248] mb-2">{pick(DIRECTIONS_COPY.email, lang)}</p>
              <a
                href={`mailto:${email}`}
                className="inline-flex items-center gap-2 text-base text-[#2C2621] hover:text-[#C16542] transition-colors break-all"
                data-testid={`${testIdPrefix}-directions-email`}
              >
                <Mail className="w-3.5 h-3.5 text-[#C16542]" strokeWidth={1.6} />
                {email}
              </a>
            </div>
          </section>

          {/* How to get there */}
          <section data-testid={`${testIdPrefix}-directions-how`}>
            <p className="overline !text-[#5C5248] mb-3">{pick(DIRECTIONS_COPY.how, lang)}</p>
            <ul className="space-y-3 text-sm leading-relaxed">
              {loc.getting_there?.car && (
                <li className="flex gap-3">
                  <Car className="w-4 h-4 mt-1 text-[#C16542] shrink-0" strokeWidth={1.6} />
                  <div>
                    <p className="text-[10px] tracking-[0.22em] uppercase text-[#5C5248] mb-0.5">
                      {pick(DIRECTIONS_COPY.by_car, lang)}
                    </p>
                    <p className="text-[#2C2621]">{pick(loc.getting_there.car, lang)}</p>
                  </div>
                </li>
              )}
              {loc.getting_there?.transport && (
                <li className="flex gap-3">
                  <Navigation className="w-4 h-4 mt-1 text-[#C16542] shrink-0" strokeWidth={1.6} />
                  <div>
                    <p className="text-[10px] tracking-[0.22em] uppercase text-[#5C5248] mb-0.5">
                      {pick(DIRECTIONS_COPY.by_transport, lang)}
                    </p>
                    <p className="text-[#2C2621]">{pick(loc.getting_there.transport, lang)}</p>
                  </div>
                </li>
              )}
              {loc.getting_there?.airport && (
                <li className="flex gap-3">
                  <MapPin className="w-4 h-4 mt-1 text-[#C16542] shrink-0" strokeWidth={1.6} />
                  <div>
                    <p className="text-[10px] tracking-[0.22em] uppercase text-[#5C5248] mb-0.5">
                      {pick(DIRECTIONS_COPY.by_air, lang)}
                    </p>
                    <p className="text-[#2C2621]">{pick(loc.getting_there.airport, lang)}</p>
                  </div>
                </li>
              )}
            </ul>
          </section>
        </div>

        {/* Footer — nav buttons */}
        <div className="px-5 sm:px-8 py-4 border-t border-[#2C2621]/10 bg-[#F2EBE1]/40 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <a
            href={gmapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#2C2621] hover:bg-[#1A1513] text-[#FDFBF7] text-[11px] tracking-[0.22em] uppercase transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C16542]"
            data-testid={`${testIdPrefix}-directions-gmaps`}
          >
            <Navigation className="w-3.5 h-3.5" strokeWidth={1.7} />
            {pick(DIRECTIONS_COPY.gmaps, lang)}
          </a>
          <a
            href={appleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-4 py-3 border border-[#2C2621]/25 hover:border-[#C16542] text-[#2C2621] hover:text-[#C16542] text-[11px] tracking-[0.22em] uppercase transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C16542]"
            data-testid={`${testIdPrefix}-directions-apple`}
          >
            <Apple className="w-3.5 h-3.5" strokeWidth={1.7} />
            {pick(DIRECTIONS_COPY.apple, lang)}
          </a>
          <a
            href={wazeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-4 py-3 border border-[#2C2621]/25 hover:border-[#C16542] text-[#2C2621] hover:text-[#C16542] text-[11px] tracking-[0.22em] uppercase transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C16542]"
            data-testid={`${testIdPrefix}-directions-waze`}
          >
            <Car className="w-3.5 h-3.5" strokeWidth={1.7} />
            {pick(DIRECTIONS_COPY.waze, lang)}
          </a>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(dialog, document.body);
};

const ShareDialog = ({ hotelName, hotelUrl, onClose, testIdPrefix }) => {
  const [copied, setCopied] = useState(false);
  const subject = `Grup Xaluca · ${hotelName}`;
  const body = `${hotelName}\n${hotelUrl}\n\nDiscover more about this property at Grup Xaluca.`;
  const emailHref = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(`${hotelName} — ${hotelUrl}`)}`;

  const onCopy = async (e) => {
    stop(e);
    try {
      await navigator.clipboard.writeText(hotelUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Share ${hotelName}`}
      onClick={stop}
      className="absolute z-[30] left-1/2 -translate-x-1/2 bottom-[calc(100%+10px)] w-[260px] sm:w-[290px] bg-[#1A1513] border border-[#D4A373]/35 shadow-[0_18px_38px_-12px_rgba(0,0,0,0.65)] p-4"
      data-testid={`${testIdPrefix}-share-dialog`}
    >
      <div className="flex items-center justify-between">
        <p className="text-[10px] tracking-[0.28em] uppercase text-[#D4A373]">Share this hotel</p>
        <button
          type="button"
          onClick={(e) => { stop(e); onClose(); }}
          className="text-[#FDFBF7]/70 hover:text-[#FDFBF7]"
          aria-label="Close share dialog"
          data-testid={`${testIdPrefix}-share-close`}
        >
          <X className="w-4 h-4" strokeWidth={1.6} />
        </button>
      </div>

      <ul className="mt-3 divide-y divide-[#FDFBF7]/8">
        <li>
          <a
            href={emailHref}
            onClick={(e) => { e.stopPropagation(); }}
            className="flex items-center gap-3 py-2.5 text-sm text-[#FDFBF7]/95 hover:text-[#D4A373] transition-colors"
            data-testid={`${testIdPrefix}-share-email`}
          >
            <Mail className="w-4 h-4 text-[#D4A373]" strokeWidth={1.5} />
            <span>Email</span>
          </a>
        </li>
        <li>
          <button
            type="button"
            onClick={onCopy}
            className="w-full flex items-center gap-3 py-2.5 text-left text-sm text-[#FDFBF7]/95 hover:text-[#D4A373] transition-colors"
            data-testid={`${testIdPrefix}-share-copy`}
          >
            {copied ? (
              <Check className="w-4 h-4 text-[#D4A373]" strokeWidth={1.8} />
            ) : (
              <Copy className="w-4 h-4 text-[#D4A373]" strokeWidth={1.5} />
            )}
            <span>{copied ? "Link copied" : "Copy link"}</span>
          </button>
        </li>
        <li>
          <a
            href={whatsappHref}
            target="_blank"
            rel="noreferrer noopener"
            onClick={(e) => { e.stopPropagation(); }}
            className="flex items-center gap-3 py-2.5 text-sm text-[#FDFBF7]/95 hover:text-[#D4A373] transition-colors"
            data-testid={`${testIdPrefix}-share-whatsapp`}
          >
            <MessageCircle className="w-4 h-4 text-[#D4A373]" strokeWidth={1.5} />
            <span>WhatsApp</span>
          </a>
        </li>
      </ul>
    </div>
  );
};

const PropertyCardActions = ({ hotelSlug, hotelName, testIdPrefix = "card-actions" }) => {
  const [shareOpen, setShareOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [directionsOpen, setDirectionsOpen] = useState(false);

  const hotelUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/hotels/${hotelSlug}`
      : `/hotels/${hotelSlug}`;

  const openChat = (e) => {
    stop(e);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("xaluca:open-chat"));
    }
  };

  const toggleShare = (e) => {
    stop(e);
    setShareOpen((cur) => !cur);
  };

  const openPricing = (e) => {
    stop(e);
    setPricingOpen(true);
  };

  const openDirections = (e) => {
    stop(e);
    setDirectionsOpen(true);
  };

  // Close share dialog on outside clicks anywhere on the document.
  useEffect(() => {
    if (!shareOpen) return undefined;
    const onDocClick = () => setShareOpen(false);
    window.addEventListener("click", onDocClick);
    return () => window.removeEventListener("click", onDocClick);
  }, [shareOpen]);

  return (
    <div
      className="relative mt-4 flex items-center gap-2.5"
      onClick={(e) => e.stopPropagation()}
      data-testid={testIdPrefix}
    >
      <Link
        to="/contact"
        onClick={(e) => e.stopPropagation()}
        className={ICON_BTN}
        aria-label={`Email Grup Xaluca about ${hotelName}`}
        data-testid={`${testIdPrefix}-email`}
      >
        <Mail className="w-4 h-4" strokeWidth={1.5} />
      </Link>

      <a
        href=""
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
        className={ICON_BTN}
        aria-label={`Call about ${hotelName}`}
        data-testid={`${testIdPrefix}-phone`}
      >
        <Phone className="w-4 h-4" strokeWidth={1.5} />
      </a>

      <button
        type="button"
        onClick={openChat}
        className={ICON_BTN}
        aria-label="Open virtual concierge"
        data-testid={`${testIdPrefix}-assistant`}
      >
        <Headset className="w-4 h-4" strokeWidth={1.5} />
      </button>

      <button
        type="button"
        onClick={openPricing}
        className={`${ICON_BTN} ${pricingOpen ? "border-[#D4A373] text-[#D4A373] bg-[#D4A373]/15" : ""}`}
        aria-label={`View pricing for ${hotelName}`}
        aria-haspopup="dialog"
        aria-expanded={pricingOpen}
        data-testid={`${testIdPrefix}-pricing`}
      >
        <Wallet className="w-4 h-4" strokeWidth={1.5} />
      </button>

      <button
        type="button"
        onClick={openDirections}
        className={`${ICON_BTN} ${directionsOpen ? "border-[#D4A373] text-[#D4A373] bg-[#D4A373]/15" : ""}`}
        aria-label={`Directions to ${hotelName}`}
        aria-haspopup="dialog"
        aria-expanded={directionsOpen}
        data-testid={`${testIdPrefix}-directions`}
      >
        <MapPin className="w-4 h-4" strokeWidth={1.5} />
      </button>

      <button
        type="button"
        onClick={toggleShare}
        className={`${ICON_BTN} ${shareOpen ? "border-[#D4A373] text-[#D4A373] bg-[#D4A373]/15" : ""}`}
        aria-label={`Share ${hotelName}`}
        aria-haspopup="dialog"
        aria-expanded={shareOpen}
        data-testid={`${testIdPrefix}-share`}
      >
        <Share2 className="w-4 h-4" strokeWidth={1.5} />
      </button>

      {shareOpen ? (
        <ShareDialog
          hotelName={hotelName}
          hotelUrl={hotelUrl}
          onClose={() => setShareOpen(false)}
          testIdPrefix={testIdPrefix}
        />
      ) : null}

      {pricingOpen ? (
        <PricingDialog
          hotelSlug={hotelSlug}
          hotelName={hotelName}
          onClose={() => setPricingOpen(false)}
          testIdPrefix={testIdPrefix}
        />
      ) : null}

      {directionsOpen ? (
        <DirectionsDialog
          hotelSlug={hotelSlug}
          hotelName={hotelName}
          onClose={() => setDirectionsOpen(false)}
          testIdPrefix={testIdPrefix}
        />
      ) : null}
    </div>
  );
};

export default PropertyCardActions;
